import { X } from '../../icons/lucide/X';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { Button } from '../Button';
import {
  NOTIFICATION_DISMISS_ICON_SIZE,
  NOTIFICATION_LEADING_ICON_SIZE,
  resolveNotificationDismissColor,
  resolveNotificationIconColor,
} from './Notification.icons';
import type { NotificationAction, NotificationProps } from './Notification.types';
import {
  ACTION_BUTTON_SIZE,
  notificationDescriptionVariants,
  notificationTimestampVariants,
  notificationTitleVariants,
  notificationVariants,
  PRIMARY_ACTION_VARIANT,
  SECONDARY_ACTION_VARIANT,
} from './Notification.variants';

/**
 * Cross-platform Notification card — an inbox / feed surface. Renders
 * identically on web and React Native.
 *
 * Presentational only: it owns no fetching, read-state persistence, or list
 * virtualization. The host feed maps its data onto these props and wires
 * `onPress` / action / dismiss callbacks. Mirrors Figma "Flashka Design
 * System" (`YZK9r3wDhvw1pY60KlVEIj`, "Notifications" node `1997:17374`).
 *
 * @example
 * ```tsx
 * import { Notification } from '@flashka/shared/ui';
 *
 * // Unread, with avatar + actions
 * <Notification
 *   avatar={<Avatar uri={user.photo} />}
 *   title="Anna joined your study group"
 *   description="Say hi and share a deck."
 *   timestamp="3min"
 *   onPress={openThread}
 *   primaryAction={{ label: 'View', onPress: openGroup }}
 *   secondaryAction={{ label: 'Dismiss', onPress: hide }}
 * />
 *
 * // Read, compact, icon leading
 * <Notification
 *   read
 *   size="compact"
 *   icon={Sparkles}
 *   title="Daily review ready"
 *   timestamp="9:00"
 *   onPress={openReview}
 * />
 * ```
 */
export function Notification({
  size = 'default',
  read = false,
  disabled = false,
  avatar,
  icon: Icon,
  iconColor: iconColorOverride,
  title,
  description,
  timestamp,
  media,
  primaryAction,
  secondaryAction,
  actions,
  dismissable = false,
  onDismiss,
  dismissAccessibilityLabel,
  onPress,
  className,
  titleClassName,
  descriptionClassName,
  timestampClassName,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  onHoverIn,
  onHoverOut,
}: NotificationProps) {
  const { isDark } = useTheme();

  const containerClass = cn(notificationVariants({ read, size, disabled }), className);

  const iconColor = iconColorOverride ?? resolveNotificationIconColor(isDark);
  const dismissColor = resolveNotificationDismissColor(isDark);

  const showDescription =
    size !== 'compact' && description != null && description !== false;
  const hasPresetActions = primaryAction != null || secondaryAction != null;
  const hasCustomActions = !hasPresetActions && actions != null;
  const hasActions = hasPresetActions || hasCustomActions;

  // Indent media + action rows to align with the text column — past the
  // leading slot width + the 12px row gap. Avatar (40px) → 52px; icon (22px)
  // → 34px; no leading slot → no indent.
  const sectionIndent = avatar != null ? 'pl-[52px]' : Icon != null ? 'pl-[34px]' : '';

  // Leading visual top-padding: small icons drop 8px to sit by the title; a
  // tall avatar spans the two text lines, so it only needs the 8px nudge at
  // default/large (compact is short enough to top-align flush).
  const leadingTopPad = Icon != null ? 'pt-[8px]' : size === 'compact' ? '' : 'pt-[8px]';

  const titleNode = isTextLike(title) ? (
    <Text
      testID={subTestID(testID, 'title')}
      className={cn(notificationTitleVariants({ read }), titleClassName)}>
      {title}
    </Text>
  ) : (
    title
  );

  const descriptionNode = !showDescription ? null : isTextLike(description) ? (
    <Text
      testID={subTestID(testID, 'description')}
      className={cn(notificationDescriptionVariants({ read }), descriptionClassName)}>
      {description}
    </Text>
  ) : (
    description
  );

  const timestampNode = isTextLike(timestamp) ? (
    <Text
      testID={subTestID(testID, 'timestamp')}
      className={cn(notificationTimestampVariants({ read }), timestampClassName)}>
      {timestamp}
    </Text>
  ) : (
    timestamp
  );

  // Stop nested interactive presses (actions / dismiss) from also firing the
  // card's onPress. `stopPropagation` exists on both the web MouseEvent and the
  // native GestureResponderEvent (see PressEvent).
  const stopAnd = (handler?: (event: PressEvent) => void) => (event: PressEvent) => {
    event.stopPropagation();
    handler?.(event);
  };

  const content = (
    <>
      <View className="w-full flex flex-row items-start gap-[12px]">
        {avatar != null ? (
          <View className={cn('shrink-0', leadingTopPad)}>{avatar}</View>
        ) : Icon != null ? (
          <View className={cn('shrink-0', leadingTopPad)}>
            <Icon color={iconColor} size={NOTIFICATION_LEADING_ICON_SIZE} />
          </View>
        ) : null}

        <View className="min-w-0 flex-1 flex flex-col">
          {titleNode}
          {descriptionNode}
          {timestampNode}
        </View>

        {!read || dismissable ? (
          <View className="shrink-0 self-center flex flex-row items-center gap-[8px]">
            {!read ? <View className="h-2 w-2 rounded-full bg-primary-500" /> : null}
            {dismissable ? (
              <Pressable
                as="div"
                onPress={stopAnd(onDismiss)}
                disabled={disabled}
                accessibilityLabel={dismissAccessibilityLabel ?? 'Dismiss'}
                accessibilityRole="button"
                testID={subTestID(testID, 'close')}
                className="shrink-0 web:transition-opacity web:hover:opacity-70">
                <X color={dismissColor} size={NOTIFICATION_DISMISS_ICON_SIZE} />
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </View>

      {media != null ? (
        <View className={cn('w-full', sectionIndent)}>
          <View className="h-[162px] w-full overflow-hidden rounded-[20px] border border-primary-200">
            {media}
          </View>
        </View>
      ) : null}

      {hasPresetActions ? (
        <View className={cn('flex flex-row items-center gap-[8px]', sectionIndent)}>
          {primaryAction != null ? (
            <ActionButton
              action={primaryAction}
              variant={PRIMARY_ACTION_VARIANT}
              stopAnd={stopAnd}
              testID={subTestID(testID, 'primary')}
            />
          ) : null}
          {secondaryAction != null ? (
            <ActionButton
              action={secondaryAction}
              variant={SECONDARY_ACTION_VARIANT}
              stopAnd={stopAnd}
              testID={subTestID(testID, 'secondary')}
            />
          ) : null}
        </View>
      ) : hasCustomActions ? (
        <View className={cn('flex flex-row items-center gap-[8px]', sectionIndent)}>
          {actions}
        </View>
      ) : null}
    </>
  );

  if (onPress != null) {
    // A pressable card that contains its own buttons / dismiss can't be a
    // native <button> (invalid nested-button HTML) — render `as="div"`
    // (focusable role=button) in that case; otherwise a real <button> for full
    // keyboard semantics. Native ignores `as` entirely.
    const pressableAs = dismissable || hasActions ? 'div' : 'button';
    return (
      <Pressable
        as={pressableAs}
        className={containerClass}
        disabled={disabled}
        onPress={onPress}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        testID={testID}
        webProps={webProps}
        nativeProps={nativeProps}>
        {content}
      </Pressable>
    );
  }

  return (
    <View
      className={containerClass}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}>
      {content}
    </View>
  );
}
Notification.displayName = 'Notification';

/** Renders one action button mapped to the shared Button (size `s`). */
function ActionButton({
  action,
  variant,
  stopAnd,
  testID,
}: {
  action: NotificationAction;
  variant: typeof PRIMARY_ACTION_VARIANT | typeof SECONDARY_ACTION_VARIANT;
  stopAnd: (handler?: (event: PressEvent) => void) => (event: PressEvent) => void;
  testID?: string;
}) {
  return (
    <Button
      variant={variant}
      size={ACTION_BUTTON_SIZE}
      leftIcon={action.leftIcon}
      disabled={action.disabled}
      testID={testID}
      onPress={stopAnd(action.onPress)}>
      {action.label}
    </Button>
  );
}

/**
 * String / number / array-of-those render inside a styled `<Text>` (required
 * on native). Any other JSX is rendered as-is — the caller owns its styling.
 */
function isTextLike(node: unknown): boolean {
  if (node == null || typeof node === 'boolean') return false;
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.every(isTextLike);
  return false;
}
